// The Help section's table of contents (route /help/:article): every article's id (its URL
// segment), title, group and search keywords. The article bodies are help/content/*.tsx; the
// per-field help modals (help/topics.ts) link into these articles, optionally at a section anchor.
// Pure; tests/unit/help.test.ts checks every topic points at a real article.

export type ArticleGroup = "Getting started" | "Customers" | "Billing" | "Finance" | "Operations" | "For developers";

export const ARTICLE_GROUPS: readonly ArticleGroup[] = [
  "Getting started",
  "Customers",
  "Billing",
  "Finance",
  "Operations",
  "For developers",
];

export interface Article {
  id: string;
  title: string;
  group: ArticleGroup;
  /** One sentence, shown in search results and used for matching. */
  summary: string;
  /** Extra words people might search for. */
  keywords: readonly string[];
  /** Section anchors inside the article that help topics may link to. */
  sections: readonly string[];
}

export const ARTICLES = [
  {
    id: "what-nexus-does",
    title: "What Nexus by AllCode does",
    group: "Getting started",
    summary: "Nexus puts a metering proxy in front of Amazon Bedrock in each customer's AWS account and bills the usage.",
    keywords: ["overview", "proxy", "bedrock", "usage api", "architecture", "home"],
    sections: ["the-parts", "the-screens", "overview-screen"],
  },
  {
    id: "signing-in-and-roles",
    title: "Signing in and roles",
    group: "Getting started",
    summary: "How staff sign in with AWS IAM Identity Center and which groups open which screens.",
    keywords: ["sign in", "login", "saml", "identity center", "role", "permission", "super-admin", "billing admin", "installer", "session", "sign out", "theme", "dark mode"],
    sections: ["sessions", "roles", "what-each-role-sees"],
  },
  {
    id: "glossary",
    title: "Glossary",
    group: "Getting started",
    summary: "The words the admin app uses, in one place.",
    keywords: ["terms", "definitions", "words", "customer", "installation", "cost basis", "markup", "margin", "cur", "period"],
    sections: [],
  },
  {
    id: "adding-a-customer",
    title: "Adding a customer",
    group: "Customers",
    summary: "Create a customer, then give it an installation, sign-in and a rate plan.",
    keywords: ["new customer", "org", "create", "slug", "status", "suspend", "restore", "settings"],
    sections: ["status", "settings"],
  },
  {
    id: "users-teams-and-sign-in",
    title: "Users, teams and sign-in",
    group: "Customers",
    summary: "How a customer's developers are identified: AWS sign-in, OIDC providers and API keys.",
    keywords: ["users", "teams", "aws sign-in", "oidc", "api key", "credential", "identity", "principal", "pattern", "team mapping", "trusted token", "key limit"],
    sections: ["users-and-teams", "aws-sign-in", "oidc", "proxy-sign-in", "api-keys"],
  },
  {
    id: "installations",
    title: "Installations",
    group: "Customers",
    summary: "A proxy installation in a customer's AWS account: its id, secret, health and rotation.",
    keywords: ["installation", "proxy", "aws account", "secret", "rotate", "heartbeat", "health", "stale", "revoke", "billing attribution", "sts", "cost explorer", "cur"],
    sections: ["health", "secrets", "billing-attribution-in-aws-billing"],
  },
  {
    id: "quotas-and-notifications",
    title: "Quotas and notifications",
    group: "Customers",
    summary: "Caps on spend, tokens or requests per organization, team or person, their standing, grants, and the webhooks that announce them.",
    keywords: ["quota", "cap", "limit", "budget", "grant", "standing", "exhausted", "grace", "warning", "guardrail", "webhook", "slack", "notification", "fleet"],
    sections: ["quotas", "standing", "grants", "fleet", "notifications"],
  },
  {
    id: "pricing-and-markup",
    title: "Pricing and markup",
    group: "Billing",
    summary: "How usage becomes an invoice, and why a 30 % markup is a 23.1 % margin.",
    keywords: ["price", "markup", "margin", "list price", "cost basis", "invoice", "profit"],
    sections: ["tokens-to-invoice", "markup-is-not-margin", "cost-basis"],
  },
  {
    id: "model-prices",
    title: "Model prices",
    group: "Billing",
    summary: "The AWS list price of each Bedrock model, by date, that usage is priced at.",
    keywords: ["price book", "model", "bedrock", "per million tokens", "input", "output", "cache", "valid from"],
    sections: ["adding-a-price"],
  },
  {
    id: "rate-plans",
    title: "Rate plans",
    group: "Billing",
    summary: "A rate plan turns AWS cost into the customer's price: markup, per-model rates and tiers.",
    keywords: ["rate plan", "plan", "draft", "activate", "retire", "clone", "tier", "volume", "rates", "default plan", "assignment", "billing mode", "who pays"],
    sections: ["plan-lifecycle", "rates-and-tiers", "assigning-a-plan", "billing-mode", "cost-visibility"],
  },
  {
    id: "trials-and-credits",
    title: "Trials and credits",
    group: "Billing",
    summary: "Free usage up to a dollar or token limit between two dates.",
    keywords: ["trial", "credit", "free", "burn-down", "limit", "cancel"],
    sections: ["burn-down"],
  },
  {
    id: "usage-adjustments",
    title: "Usage adjustments",
    group: "Billing",
    summary: "Credits and corrections to a month's billed amount, and who approves them.",
    keywords: ["adjustment", "credit", "debit", "correction", "approve", "void", "refund"],
    sections: ["approving"],
  },
  {
    id: "closing-a-month",
    title: "Closing a month and invoices",
    group: "Billing",
    summary: "What closing a month does, when you can force it, and how invoices and statements are built.",
    keywords: ["close", "period", "invoice", "statement", "billing period", "forced", "force reason", "open", "late events", "month end"],
    sections: ["before-you-close", "forcing", "invoices-and-statements"],
  },
  {
    id: "revenue-and-margin",
    title: "Revenue and margin",
    group: "Finance",
    summary: "Billed, AWS cost and gross profit by customer, model and month, and the forecast.",
    keywords: ["revenue", "gross profit", "margin", "markup", "forecast", "not billed", "provisional", "open months"],
    sections: ["reading-the-chart", "not-billed", "forecast"],
  },
  {
    id: "aws-costs",
    title: "Where AWS costs come from",
    group: "Finance",
    summary: "List price from the price book, or the actual bill from the Cost and Usage Report (CUR).",
    keywords: ["cur", "cost and usage report", "expense", "aws bill", "basis", "usage type", "finalize", "restate", "s3", "athena"],
    sections: ["expense-basis", "cur-sources", "usage-types", "finalizing"],
  },
  {
    id: "reconciliation",
    title: "Reconciliation",
    group: "Finance",
    summary: "Checks that the proxy's metered usage matches what Bedrock and the CUR recorded.",
    keywords: ["reconcile", "drift", "completeness", "bedrock", "invocation logs", "cloudwatch", "counters"],
    sections: ["drift", "completeness"],
  },
  {
    id: "installer",
    title: "Installing into an AWS account",
    group: "Operations",
    summary: "The agentic installer: link an account, review each proposed change, verify and hand over.",
    keywords: ["installer", "install", "session", "link account", "external id", "cloudformation", "approve", "change", "budget", "identity center", "sts"],
    sections: ["linking-an-account", "sessions", "approving-changes", "access-modes"],
  },
  {
    id: "assistant",
    title: "The Assistant",
    group: "Operations",
    summary: "Ask about a customer's usage, quotas, people and settings in plain words; what it can do, who pays, and why it may refuse.",
    keywords: ["assistant", "agent", "ai", "chat", "conversation", "ask", "question", "budget", "paused", "ended", "turned off", "disabled", "claude code", "mcp"],
    sections: ["what-it-can-do", "conversations", "claude-code", "who-pays", "refusals", "agent-disabled", "session-terminal", "agent-daily-budget-exhausted"],
  },
  {
    id: "assistant-approvals",
    title: "Approving the Assistant's changes",
    group: "Operations",
    summary: "Every change the Assistant proposes waits for a person: review what would change, type the confirmation a risky one asks for, approve or reject.",
    keywords: ["assistant", "approve", "approval", "reject", "change", "proposal", "confirm", "confirmation", "risky", "expired", "changed since proposed", "reload"],
    sections: ["how-it-works", "reviewing", "confirmations", "approving", "rejecting", "when-it-fails"],
  },
  {
    id: "audit-log",
    title: "Audit log",
    group: "Operations",
    summary: "Every change made in the admin app, who made it and when; filters and CSV export.",
    keywords: ["audit", "trail", "who", "history", "export", "csv", "actor", "action", "activity", "guardrail", "triggered"],
    sections: ["what-you-see", "customer-activity"],
  },
  {
    id: "claude-code-and-codex",
    title: "Claude Code and Codex setup",
    group: "For developers",
    summary: "Point a developer's Claude Code or Codex at the customer's Nexus proxy.",
    keywords: ["claude code", "codex", "developer", "client", "setup", "base url", "aws profile", "token", "settings.json", "models", "fallback"],
    sections: ["claude-code", "codex", "configuring-models"],
  },
] as const satisfies readonly Article[];

export type ArticleId = (typeof ARTICLES)[number]["id"];

export const ARTICLE_BY_ID: ReadonlyMap<string, Article> = new Map(ARTICLES.map((article) => [article.id, article]));

export function articleById(id: string): Article | null {
  return ARTICLE_BY_ID.get(id) ?? null;
}

/** The Help path of an article, optionally at a section anchor. */
export function articleHref(id: ArticleId, section?: string): string {
  return section ? `/help/${id}#${section}` : `/help/${id}`;
}

/** Articles whose title, summary or keywords contain every word of the query, title matches first. */
export function searchArticles(query: string): Article[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter((word) => word !== "");
  if (words.length === 0) return [...ARTICLES];
  const scored: { article: Article; score: number }[] = [];
  for (const article of ARTICLES) {
    const title = article.title.toLowerCase();
    const haystack = [title, article.summary.toLowerCase(), ...article.keywords.map((k) => k.toLowerCase())].join(" \u0000 ");
    if (!words.every((word) => haystack.includes(word))) continue;
    const score = words.reduce((sum, word) => sum + (title.includes(word) ? 2 : 0) + (article.keywords.some((k) => k.toLowerCase().includes(word)) ? 1 : 0), 0);
    scored.push({ article, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((s) => s.article);
}

/** Articles grouped in sidebar order, dropping empty groups. */
export function groupArticles(articles: readonly Article[]): { group: ArticleGroup; articles: Article[] }[] {
  return ARTICLE_GROUPS.map((group) => ({ group, articles: articles.filter((a) => a.group === group) })).filter(
    (entry) => entry.articles.length > 0,
  );
}
